import { createContext, useCallback, useContext, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { aggregateSunlight, sceneShadowHash } from '../scene3d/shadow/shadowAggregator';

/**
 * ShadowHeatmapContext - geteiltes Jahres-Sonnenstunden-Grid (#606)
 *
 * Die Berechnung stößt der Nutzer im „Sonne & Schatten"-Panel an
 * (Jahresübersicht → Berechnen). Das Ergebnis-Grid wird hier gehalten, damit
 * sowohl das HeatmapOverlay (Canvas) als auch der Licht-Abgleich
 * (SunlightWarningBadge im Eigenschaften-Panel) dieselbe Auswertung lesen.
 *
 * Läuft bevorzugt im Web Worker (Main-Thread frei); ohne Worker (Tests/SSR)
 * synchron. Ergebnis wird per SceneModel-Hash gecacht — Neuberechnung nur bei
 * Geometrie-/Standort-Änderung.
 */

const ShadowHeatmapContext = createContext(null);

const IDLE = {
  grid: null,
  status: 'idle',
  progress: 0,
  compute: () => {},
  clear: () => {},
  isStale: () => false,
};

export function ShadowHeatmapProvider({ children }) {
  const [grid, setGrid] = useState(null);
  const [status, setStatus] = useState('idle'); // idle | computing | ready | error
  const [progress, setProgress] = useState(0);
  const hashRef = useRef(null);
  const workerRef = useRef(null);

  const clear = useCallback(() => {
    workerRef.current?.terminate();
    workerRef.current = null;
    hashRef.current = null;
    setGrid(null);
    setStatus('idle');
    setProgress(0);
  }, []);

  const finish = useCallback((result, hash) => {
    hashRef.current = result ? hash : null;
    setGrid(result);
    setStatus(result ? 'ready' : 'idle');
    setProgress(1);
  }, []);

  const compute = useCallback(
    async (sceneModel, coords) => {
      if (!sceneModel || !coords) return;
      const hash = sceneShadowHash(sceneModel, coords);
      if (hash === hashRef.current && grid) return; // Cache-Treffer

      setStatus('computing');
      setProgress(0);
      const options = { latitude: coords.latitude, longitude: coords.longitude };

      if (typeof Worker !== 'undefined') {
        try {
          const { default: createHeatmapWorker } = await import(
            '../scene3d/shadow/createHeatmapWorker'
          );
          workerRef.current?.terminate();
          const worker = createHeatmapWorker();
          workerRef.current = worker;
          worker.onmessage = (event) => {
            const msg = event.data;
            if (msg.type === 'progress') {
              setProgress(msg.fraction);
            } else if (msg.type === 'result') {
              finish(msg.grid, hash);
              worker.terminate();
              workerRef.current = null;
            } else if (msg.type === 'error') {
              setStatus('error');
              worker.terminate();
              workerRef.current = null;
            }
          };
          worker.onerror = () => {
            setStatus('error');
            worker.terminate();
            workerRef.current = null;
          };
          worker.postMessage({ sceneModel, options });
          return;
        } catch {
          // kein Worker verfügbar → synchron
        }
      }

      const result = aggregateSunlight(sceneModel, { ...options, onProgress: setProgress });
      finish(result, hash);
    },
    [grid, finish]
  );

  const isStale = useCallback(
    (sceneModel, coords) =>
      grid != null && sceneShadowHash(sceneModel, coords) !== hashRef.current,
    [grid]
  );

  const value = { grid, status, progress, compute, clear, isStale };
  return <ShadowHeatmapContext.Provider value={value}>{children}</ShadowHeatmapContext.Provider>;
}

ShadowHeatmapProvider.propTypes = {
  children: PropTypes.node,
};

/** Kein Provider (z. B. isolierte Tests) → ruhiger Leerlauf-Zustand. */
export function useShadowHeatmap() {
  return useContext(ShadowHeatmapContext) ?? IDLE;
}

export default ShadowHeatmapContext;
